import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  MenuItem,
  Tabs as MuiTabs,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import { AutoAwesomeRounded, OpenInNew } from '@mui/icons-material';
import Page from '../../components/shared/Page/Page';
import Loading from '../../components/shared/Loading/Loading';
import StyledButton from '../../components/shared/Button/StyledButton';
import StyledContentLayout from '../../styles/StyledContentLayout';
import StyledConfettiExplosion from '../../components/agents/AgentForm/StyledConfettiExplosion';
import {
  pricingPlansCall,
  pricingPlansChat,
  pricingPlansBundle,
} from '../../config/billingPlansConfig';
import PricingCardsCarousel from '../../components/pricing/PricingCardsCarousel';
import PricingCard from '../../components/pricing/PricingCard';
import PricingTierSelect from '../../components/pricing/PricingTierSelect';
import StyledTab from '../../components/shared/Tabs/StyledTab';
import useAuth from '../../hooks/useAuth';
import { useSubscription } from '../../contexes/SubscriptionProvider';
import { shouldShowChatUserCallEnterpriseNotice } from '../../helpers/pricingUpgradeRules';
import { Price } from '../../interfaces/Price';
import { useTranslation } from '../../hooks/useTranslation';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsEvents } from '../../enums/AnalyticsEvents';
import { priceToPerMoShort } from '../../helpers/pricingCardCopy';
import { bundleTierCapacityMenuSegment } from '../../helpers/billingTierMenuLabels';
import {
  getVisibleV2PlanTabs,
  isEnterpriseSubscriber,
  isPaidPlanSubscriber,
} from '../../helpers/billingPlanSwitchUi';
import { isPaidMeteredPlan } from '../../helpers/creditUsageDisplay';
import { isLocalHost } from '../../helpers/environmentHelper';
import { changeSubscriptionPlan } from '../../services/billingService';
import { useSnackBar } from '../../hooks/useSnackBar';
import BillingOverageLimitCard from '../../components/billing/BillingOverageLimitCard';
import { USAGE_DATA_REFRESH_EVENT } from '../../helpers/usageEvents';
import { enforcePlanAgentLimits } from '../../helpers/enforcePlanAgentLimits';
import { ConfettiProps } from 'react-confetti-explosion';
import { fetchUsage, UsageResponse } from '../../services/billingService';

const STRIPE_BILLING_PORTAL_URL = isLocalHost()
  ? 'https://billing.stripe.com/p/login/test_00g5lJ1zG3hebbadQQ'
  : 'https://billing.stripe.com/p/login/28o2aD6MKd517ksdQQ';

const ENTERPRISE_CHAT_CALLS_MSG =
  'Enterprise is required to add calls to your chat subscription.' as const;

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { subscription, company, refreshSubscription } = useSubscription();
  const { track } = useAnalytics();
  const showSnackBar = useSnackBar();
  const [v2Tab, setV2Tab] = useState<'call' | 'chat' | 'bundle'>('call');
  const [switchingPlanId, setSwitchingPlanId] = useState<string | null>(null);
  const [optimisticPlanId, setOptimisticPlanId] = useState<string | null>(null);
  const [confettiBurst, setConfettiBurst] = useState(0);
  const defaultTabApplied = useRef(false);
  const [isBillingUiReady, setIsBillingUiReady] = useState(false);
  const [usageData, setUsageData] = useState<UsageResponse | null>(null);
  const allPlans = useMemo<Price[]>(
    () =>
      [
        ...pricingPlansCall,
        ...pricingPlansChat,
        ...pricingPlansBundle,
      ] as Price[],
    []
  );
  const effectiveCurrentPlanId = optimisticPlanId ?? company?.plan_id ?? null;

  useEffect(() => {
    if (!currentUser?.company_id) {
      setUsageData(null);
      return;
    }
    let cancelled = false;
    fetchUsage(currentUser.company_id).then(data => {
      if (!cancelled) setUsageData(data);
    });
    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.company_id,
    company?.plan_id,
    company?.overage_credits_limit,
  ]);

  useEffect(() => {
    if (company?.plan_id) {
      setOptimisticPlanId(company.plan_id);
    }
  }, [company?.plan_id]);

  const isEnterprise = isEnterpriseSubscriber(company, subscription);

  const visibleTabs = useMemo(
    () => getVisibleV2PlanTabs(company, subscription),
    [company, subscription]
  );

  useEffect(() => {
    if (!visibleTabs.includes(v2Tab)) {
      setV2Tab(visibleTabs[0] ?? 'call');
    }
  }, [visibleTabs, v2Tab]);

  const effectivePlanTab = isEnterprise
    ? (company?.plan_type ?? 'call')
    : visibleTabs.includes(v2Tab)
      ? v2Tab
      : (visibleTabs[0] ?? 'call');

  const paidForSwitch = isPaidPlanSubscriber(company, subscription);

  const handleSwitchPlan = useCallback(
    async (plan: Price) => {
      if (!currentUser?.id) return;
      setSwitchingPlanId(plan.id);
      try {
        await changeSubscriptionPlan(
          currentUser.id,
          plan.id,
          currentUser.company_id
        );
        await enforcePlanAgentLimits(currentUser);
        setOptimisticPlanId(plan.id);
        setConfettiBurst(prev => prev + 1);
        window.dispatchEvent(new Event(USAGE_DATA_REFRESH_EVENT));
        showSnackBar(t('Plan updated successfully'), 'success');
        await refreshSubscription();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : t('Error');
        showSnackBar(msg, 'error');
      } finally {
        setSwitchingPlanId(null);
      }
    },
    [currentUser?.id, refreshSubscription, showSnackBar, t]
  );

  const planSwitchContext =
    paidForSwitch && currentUser
      ? {
          enabled: true,
          currentPlanId: effectiveCurrentPlanId,
          switchingPlanId,
          onSwitchPlan: handleSwitchPlan,
        }
      : undefined;

  const activeV2Plans = useMemo<Price[]>(
    () =>
      (effectivePlanTab === 'call'
        ? pricingPlansCall
        : effectivePlanTab === 'chat'
          ? pricingPlansChat
          : pricingPlansBundle) as Price[],
    [effectivePlanTab]
  );

  const enterprisePlan = useMemo(
    () => activeV2Plans.find(p => p.tier === 'enterprise') ?? null,
    [activeV2Plans]
  );

  const starterPlans = useMemo(
    () => activeV2Plans.filter(p => p.tier === 'starter'),
    [activeV2Plans]
  );
  const businessPlans = useMemo(
    () => activeV2Plans.filter(p => p.tier === 'business'),
    [activeV2Plans]
  );

  const getTierNum = (id: string) => {
    const match = id.match(/_t(\d+)$/);
    return match ? Number(match[1]) : 0;
  };

  const sortedStarterPlans = useMemo(
    () => [...starterPlans].sort((a, b) => getTierNum(a.id) - getTierNum(b.id)),
    [starterPlans]
  );
  const sortedBusinessPlans = useMemo(
    () =>
      [...businessPlans].sort((a, b) => getTierNum(a.id) - getTierNum(b.id)),
    [businessPlans]
  );

  const starterDefaultPlanId = sortedStarterPlans[0]?.id ?? '';
  const businessDefaultPlanId = sortedBusinessPlans[0]?.id ?? '';

  const [selectedStarterPlanId, setSelectedStarterPlanId] =
    useState<string>(starterDefaultPlanId);
  const [selectedBusinessPlanId, setSelectedBusinessPlanId] = useState<string>(
    businessDefaultPlanId
  );

  useEffect(() => {
    const currentPlanId = effectiveCurrentPlanId;
    const currentIsStarter =
      !!currentPlanId &&
      sortedStarterPlans.some(plan => plan.id === currentPlanId);
    const currentIsBusiness =
      !!currentPlanId &&
      sortedBusinessPlans.some(plan => plan.id === currentPlanId);

    setSelectedStarterPlanId(
      currentIsStarter ? (currentPlanId as string) : starterDefaultPlanId
    );
    setSelectedBusinessPlanId(
      currentIsBusiness ? (currentPlanId as string) : businessDefaultPlanId
    );
  }, [
    starterDefaultPlanId,
    businessDefaultPlanId,
    effectivePlanTab,
    effectiveCurrentPlanId,
    sortedStarterPlans,
    sortedBusinessPlans,
  ]);

  useEffect(() => {
    if (defaultTabApplied.current) return;

    if (company?.plan_type) {
      const preferredTab = visibleTabs.includes(company.plan_type)
        ? company.plan_type
        : (visibleTabs[0] ?? 'call');
      setV2Tab(preferredTab);
      defaultTabApplied.current = true;
      setIsBillingUiReady(true);
      return;
    }

    if (visibleTabs.length > 0) {
      setV2Tab(visibleTabs[0]);
      defaultTabApplied.current = true;
      setIsBillingUiReady(true);
    }
  }, [company?.plan_type, visibleTabs]);

  const starterStartingAt = useMemo(
    () => priceToPerMoShort(sortedStarterPlans[0]?.price ?? ''),
    [sortedStarterPlans]
  );
  const businessStartingAt = useMemo(
    () => priceToPerMoShort(sortedBusinessPlans[0]?.price ?? ''),
    [sortedBusinessPlans]
  );
  const confettiProps: ConfettiProps = {
    force: 0.6,
    duration: 2500,
    particleCount: 180,
    width: 1200,
    colors: ['#041E43', '#1471BF', '#5BB4DC', '#FC027B', '#66D805'],
  };

  const subscriptionAllowsManage = useMemo(
    () =>
      !!subscription &&
      ['active', 'trialing', 'past_due'].includes(subscription.status),
    [subscription]
  );

  const showOverageLimit = useMemo(() => {
    if (!subscriptionAllowsManage || isEnterprise || !currentUser?.company_id) {
      return false;
    }
    if (usageData) return isPaidMeteredPlan(usageData);
    return ['starter', 'business'].includes(
      (company?.plan_tier ?? '').toLowerCase()
    );
  }, [
    subscriptionAllowsManage,
    isEnterprise,
    currentUser?.company_id,
    usageData,
    company?.plan_tier,
  ]);

  const currentPlanTitle = useMemo(() => {
    if (effectiveCurrentPlanId) {
      const currentPlan = allPlans.find(p => p.id === effectiveCurrentPlanId);
      if (currentPlan?.title) {
        const type =
          currentPlan.planType != null
            ? ` · ${currentPlan.planType.charAt(0).toUpperCase()}${currentPlan.planType.slice(1)}`
            : '';
        return `${currentPlan.title}${type}`;
      }
    }
    const name = subscription?.product_name?.trim();
    if (name) return name;
    if (company?.plan_tier && company?.plan_type) {
      const tier =
        company.plan_tier.charAt(0).toUpperCase() + company.plan_tier.slice(1);
      const planType =
        company.plan_type.charAt(0).toUpperCase() + company.plan_type.slice(1);
      return `${tier} · ${planType}`;
    }
    return null;
  }, [
    effectiveCurrentPlanId,
    allPlans,
    subscription?.product_name,
    company?.plan_tier,
    company?.plan_type,
  ]);

  const stripePortalUrl = `${STRIPE_BILLING_PORTAL_URL}?prefilled_email=${encodeURIComponent(currentUser?.email ?? '')}`;

  const starterSelectionReady =
    sortedStarterPlans.length === 0 ||
    sortedStarterPlans.some(p => p.id === selectedStarterPlanId);
  const businessSelectionReady =
    sortedBusinessPlans.length === 0 ||
    sortedBusinessPlans.some(p => p.id === selectedBusinessPlanId);
  const canRenderPlans =
    isBillingUiReady &&
    (isEnterprise ||
      (visibleTabs.length > 0 &&
        starterSelectionReady &&
        businessSelectionReady));

  const handleManageInStripe = () => {
    track(AnalyticsEvents.BILLING_MANAGEMENT_BUTTON_CLICKED);
    window.open(stripePortalUrl, '_blank', 'noopener,noreferrer');
  };

  const canceledEndsAt = useMemo(() => {
    if (subscription?.status !== 'canceled') return null;
    const ts = subscription.current_period_end;
    if (!ts) return null;
    return new Date(ts * 1000).toLocaleDateString();
  }, [subscription?.status, subscription?.current_period_end]);

  if (!canRenderPlans) {
    return (
      <Page title="Elora | Billing">
        <StyledContentLayout wide>
          <Loading />
        </StyledContentLayout>
      </Page>
    );
  }

  return (
    <Page title="Elora | Billing">
      <StyledContentLayout wide>
        {confettiBurst > 0 && (
          <StyledConfettiExplosion key={confettiBurst} {...confettiProps} />
        )}
        <Stack spacing={3}>
          {!isEnterprise && (
            <Box>
              <h1>{t('Billing')}</h1>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, maxWidth: 720, lineHeight: 1.6 }}
              >
                {t('Billing page intro')}
              </Typography>
            </Box>
          )}
          {subscription?.status === 'canceled' && canceledEndsAt && (
            <Alert severity="warning">
              {t('Subscription canceled ends at cycle', {
                date: canceledEndsAt,
              })}
            </Alert>
          )}

          {subscriptionAllowsManage && !isEnterprise && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: showOverageLimit ? '1fr 1fr' : 'minmax(0, 620px)',
                },
                gap: 2,
                alignItems: 'stretch',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                  bgcolor: '#FFFFFF',
                  boxShadow: '0px 10px 28px rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: { md: showOverageLimit ? 260 : undefined },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(500px 190px at 100% 0%, rgba(37, 99, 235, 0.14), transparent 66%)',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 999,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background:
                        'linear-gradient(135deg, rgba(37, 99, 235, 0.95), rgba(99, 102, 241, 0.9))',
                      boxShadow: '0 6px 14px rgba(37, 99, 235, 0.3)',
                    }}
                  >
                    <AutoAwesomeRounded
                      sx={{ fontSize: '1rem', color: '#FFFFFF' }}
                    />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Your current plan')}
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    position: 'relative',
                    mt: 0.25,
                    fontWeight: 700,
                    letterSpacing: '-0.015em',
                    color: '#0F172A',
                  }}
                >
                  {currentPlanTitle ?? t('Plan')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ position: 'relative', mt: 0.6, maxWidth: 460 }}
                >
                  {t('Usage details for your plan are on the Usage page.')}
                </Typography>
                <StyledButton
                  sx={{
                    position: 'relative',
                    mt: 'auto',
                    pt: 2.25,
                    px: 2.5,
                    minHeight: 48,
                    borderRadius: 2,
                    alignSelf: 'flex-start',
                  }}
                  variant="contained"
                  color="primary"
                  endIcon={<OpenInNew />}
                  onClick={handleManageInStripe}
                >
                  {t('Manage plan in Stripe')}
                </StyledButton>
              </Box>

              {showOverageLimit && currentUser?.company_id && (
                <BillingOverageLimitCard
                  company={company}
                  companyId={currentUser.company_id}
                  usage={usageData}
                  onSaved={async () => {
                    await refreshSubscription();
                    if (currentUser.company_id) {
                      const data = await fetchUsage(currentUser.company_id);
                      setUsageData(data);
                    }
                    window.dispatchEvent(new Event(USAGE_DATA_REFRESH_EVENT));
                  }}
                />
              )}
            </Box>
          )}

          {!isEnterprise &&
            shouldShowChatUserCallEnterpriseNotice(
              company,
              effectivePlanTab
            ) && <Alert severity="info">{t(ENTERPRISE_CHAT_CALLS_MSG)}</Alert>}
          {!isEnterprise && (
            <Box sx={{ width: '100%' }}>
              <MuiTabs
                value={Math.max(0, visibleTabs.indexOf(effectivePlanTab))}
                onChange={(_, index: number) => {
                  const next = visibleTabs[index];
                  if (next) setV2Tab(next);
                }}
                variant="fullWidth"
                aria-label={t('Plan type')}
                sx={{
                  p: 0.5,
                  borderRadius: 999,
                  bgcolor: '#FFFFFF',
                  border: '1px solid rgba(15, 23, 42, 0.06)',
                  boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.08)',
                  minHeight: 56,
                  '& .MuiTabs-indicator': { display: 'none' },
                }}
              >
                {visibleTabs.map(tab => (
                  <StyledTab
                    key={tab}
                    label={
                      tab === 'call'
                        ? t('Call')
                        : tab === 'chat'
                          ? t('Chat')
                          : t('Bundle')
                    }
                  />
                ))}
              </MuiTabs>
            </Box>
          )}
          <PricingCardsCarousel
            key={effectivePlanTab}
            scrollToPlanId={effectiveCurrentPlanId}
          >
            {!isEnterprise && sortedStarterPlans.length > 0 && (
              <PricingCard
                plan={
                  (sortedStarterPlans.find(
                    p => p.id === selectedStarterPlanId
                  ) ?? sortedStarterPlans[0]) as Price
                }
                currentUser={currentUser}
                upgradeContext={{
                  activeV2Tab: effectivePlanTab,
                  company,
                }}
                planSwitchContext={planSwitchContext}
                startingAtMonthly={starterStartingAt}
                tierSelector={
                  <PricingTierSelect
                    value={selectedStarterPlanId}
                    onChange={v => setSelectedStarterPlanId(v)}
                  >
                    {sortedStarterPlans.map(plan => {
                      const tierNum = getTierNum(plan.id);
                      const label =
                        effectivePlanTab === 'call'
                          ? `Starter T${tierNum} (${plan.minutes ?? 0} ${t('min')})`
                          : effectivePlanTab === 'chat'
                            ? `Starter T${tierNum} (${plan.messages ?? 0} ${t('msgs')})`
                            : `Starter T${tierNum} (${bundleTierCapacityMenuSegment(
                                t,
                                plan
                              )})`;

                      return (
                        <MenuItem key={plan.id} value={plan.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </PricingTierSelect>
                }
              />
            )}

            {!isEnterprise && sortedBusinessPlans.length > 0 && (
              <PricingCard
                plan={
                  (sortedBusinessPlans.find(
                    p => p.id === selectedBusinessPlanId
                  ) ?? sortedBusinessPlans[0]) as Price
                }
                currentUser={currentUser}
                upgradeContext={{
                  activeV2Tab: effectivePlanTab,
                  company,
                }}
                planSwitchContext={planSwitchContext}
                startingAtMonthly={businessStartingAt}
                tierSelector={
                  <PricingTierSelect
                    value={selectedBusinessPlanId}
                    onChange={v => setSelectedBusinessPlanId(v)}
                  >
                    {sortedBusinessPlans.map(plan => {
                      const tierNum = getTierNum(plan.id);
                      const label =
                        effectivePlanTab === 'call'
                          ? `Business T${tierNum} (${plan.minutes ?? 0} ${t('min')})`
                          : effectivePlanTab === 'chat'
                            ? `Business T${tierNum} (${plan.messages ?? 0} ${t('msgs')})`
                            : `Business T${tierNum} (${bundleTierCapacityMenuSegment(
                                t,
                                plan
                              )})`;

                      return (
                        <MenuItem key={plan.id} value={plan.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </PricingTierSelect>
                }
              />
            )}

            {enterprisePlan && (
              <PricingCard
                plan={enterprisePlan as Price}
                currentUser={currentUser}
                upgradeContext={{
                  activeV2Tab: effectivePlanTab,
                  company,
                }}
              />
            )}
          </PricingCardsCarousel>
        </Stack>
      </StyledContentLayout>
    </Page>
  );
};

export default Billing;
